import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, Loader2, ShieldCheck } from "lucide-react";
import { SiteShell } from "@/components/site/SiteShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/site/Logo";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const Route = createFileRoute("/reset-password")({
  head: () => ({
    meta: [
      { title: "Reset password — Online Tax VAT Bangladesh" },
      { name: "description", content: "Set a new password for your Online Tax VAT Bangladesh account." },
      { name: "robots", content: "noindex, nofollow" },
    ],
    links: [{ rel: "canonical", href: "https://onlinetaxvat.com/reset-password" }],
  }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" || session) {
        setHasSession(true);
        setReady(true);
      }
    });
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setHasSession(true);
      setReady(true);
    });
    return () => subscription.unsubscribe();
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (password.length < 6) return toast.error(t("resetPassword.errShort"));
    if (password !== confirm) return toast.error(t("resetPassword.errMismatch"));
    setBusy(true);
    const { error } = await supabase.auth.updateUser({ password });
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success(t("resetPassword.toastUpdated"));
    await supabase.auth.signOut();
    navigate({ to: "/login" });
  }

  return (
    <SiteShell>
      <section className="pt-14 md:pt-20 pb-20">
        <div className="container-page max-w-md">
          <div className="glass-strong rounded-3xl p-8 shadow-glow">
            <div className="text-center"><Logo className="justify-center" /></div>
            <h1 className="mt-6 font-display font-bold text-2xl text-center">{t("resetPassword.title")}</h1>
            <p className="text-sm text-muted-foreground text-center mt-1">
              <ShieldCheck className="inline h-4 w-4 text-primary mr-1" />
              {t("resetPassword.subtitle")}
            </p>

            {!ready ? (
              <div className="mt-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : !hasSession ? (
              <div className="mt-6 text-sm text-center text-muted-foreground">
                <p>{t("resetPassword.invalidLink")}</p>
                <Button asChild className="mt-4 gradient-primary text-primary-foreground"><Link to="/login">{t("resetPassword.backToLogin")}</Link></Button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="mt-6 space-y-4">
                <div>
                  <Label htmlFor="password">{t("resetPassword.newPassword")}</Label>
                  <div className="relative mt-1.5">
                    <Input id="password" type={show ? "text" : "password"} required minLength={6}
                      value={password} onChange={(e) => setPassword(e.target.value)} className="pr-10" />
                    <button type="button" onClick={() => setShow((s) => !s)}
                      aria-label={show ? t("resetPassword.hidePassword") : t("resetPassword.showPassword")}
                      className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground">
                      {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="confirm">{t("resetPassword.confirmPassword")}</Label>
                  <Input id="confirm" type={show ? "text" : "password"} required minLength={6}
                    value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5" />
                </div>
                <Button type="submit" disabled={busy} className="w-full gradient-primary text-primary-foreground shadow-glow">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("resetPassword.update")}
                </Button>
              </form>
            )}

            <p className="mt-6 text-sm text-center text-muted-foreground">
              {t("resetPassword.remembered")} <Link to="/login" className="text-primary font-semibold hover:underline">{t("resetPassword.signin")}</Link>
            </p>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
